import { html, css, LitElement } from 'lit';
import {customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import {repositoryService, versionMap} from "./repository-service";
import {Setting} from "./settings-service";

@customElement('app-root')
export class AppRoot extends LitElement {
  createRenderRoot = () => this

  _versions = new Setting<string[]>('versions', ['acv', 'kjv'])

  @property()
  get versions(): string[] { return this._versions.get() }
  set versions(v: string[]) { this._versions.set(v); this.requestUpdate() }

  @property()
  currentBook = 'Gen'

  @property()
  selectedVerse: string | null = null

  @property()
  showHistory = false

  connectedCallback() {
    super.connectedCallback()
    // Preload initial versions so book lists populate
    for (const v of this.versions) {
      repositoryService.getVersion(v).then(() => this.requestUpdate())
    }
    this.addEventListener('pane-scroll', ((e: CustomEvent) => {
      const { osisID, source } = e.detail
      this.querySelectorAll('app-bible').forEach(bible => {
        if (bible === source) return;
        (bible as any).scrollToVerse(osisID)
      })
    }) as EventListener)
    this.addEventListener('verse-clicked', ((e: CustomEvent) => {
      this._selectVerse(e.detail.osis)
      const panel = this.querySelector('app-verse-panel') as any
      panel?.addEntry(e.detail.osis, e.detail.text)
    }) as EventListener)
    this.addEventListener('verse-jump', ((e: CustomEvent) => {
      const osis = e.detail.osis
      const book = osis.split('.')[0]
      this.showHistory = false
      if (book !== this.currentBook) this.currentBook = book
      this.updateComplete.then(async () => {
        const bibles = this.querySelectorAll('app-bible')
        await Promise.all([...bibles].map((b: any) => b.updateComplete))
        // Extra frame for virtualizer to lay out
        await new Promise(r => requestAnimationFrame(r))
        this.querySelectorAll('app-bible').forEach(bible => {
          (bible as any).scrollToVerse(osis)
        })
        this._selectVerse(osis)
      })
    }) as EventListener)
    this.addEventListener('navigate-to', ((e: CustomEvent) => {
      const { book, osis } = e.detail
      this.showHistory = false
      if (this.currentBook !== book) {
        this.currentBook = book
      }
      // Wait for everything to settle, then scroll each virtualizer directly
      setTimeout(() => {
        this.querySelectorAll('app-bible').forEach(bible => {
          const indexMap = (bible as any)._indexMap as Map<string, number>
          const idx = indexMap.get(osis)
          if (idx == null) return
          const virt = bible.querySelector('lit-virtualizer') as any
          if (virt) virt.scrollToIndex(idx, 'start')
        })
      }, 300)
    }) as EventListener)
  }

  private _openNavigator() {
    const nav = this.querySelector('app-book-navigator') as any
    nav?.show()
  }

  private _selectVerse(osis: string) {
    this.querySelectorAll('app-bible-verse.selected')
      .forEach(el => el.classList.remove('selected'))
    setTimeout(() => {
      this.querySelectorAll(`app-bible-verse[data-osis="${osis}"]`)
        .forEach(el => el.classList.add('selected'))
    }, 100)
    this.selectedVerse = osis
  }

  render() {
    return html`
      <div class="toolbar">
        <app-scale></app-scale>
        <div class="toolbar-separator"></div>
        <app-version-picker .versions=${this.versions}
          @versions-changed=${(e: CustomEvent) => this.versions = e.detail.versions}
        ></app-version-picker>
        <div class="toolbar-separator"></div>
        <button class="btn" @click=${() => this._openNavigator()}>
          Navigate
        </button>
        <div class="toolbar-spacer"></div>
        <button class="btn ${this.showHistory ? 'active' : ''}" @click=${() => this.showHistory = !this.showHistory}>
          ${this.showHistory ? 'Back to Reader' : 'History'}
        </button>
        <app-theme-toggle></app-theme-toggle>
      </div>
      <app-book-navigator .versions=${this.versions}></app-book-navigator>
      <app-verse-panel class="full-view" style="display: ${this.showHistory ? 'block' : 'none'}"></app-verse-panel>
      <div class="row bible-content" style="display: ${this.showHistory ? 'none' : 'flex'}">
        <div class="row grow">
          ${this.versions.map(i => html`<app-bible .version=${i} .book=${this.currentBook}></app-bible>`)}
        </div>
        <app-bible-nav .versions=${this.versions} .book=${this.currentBook}
          @book-selected=${(evt:CustomEvent) => this.currentBook = evt.detail.book}></app-bible-nav>
      </div>
    `;
  }
}


@customElement('app-scale')
export class AppScale extends LitElement {
  createRenderRoot = () => this

  _fontSize = new Setting('font-size', 100)
  @property({type: String})
  get fontSize(){
    return this._fontSize.get()
  }
  set fontSize(v){
    this._fontSize.set(v)
    this.setSize()
    this.requestUpdate()
  }

  setSize(){
    document.body.style.fontSize = `${this.fontSize}%`;
  }

  connectedCallback() {
    super.connectedCallback();
    this.setSize()
  }

  render() {
    return html`<div id="font-size">
      <button @click=${() => this.fontSize = 80  }><span style="font-size: .55rem">A</span></button>
      <button @click=${() => this.fontSize = 100 }><span style="font-size: .75rem">A</span></button>
      <button @click=${() => this.fontSize = 140 }><span style="font-size: 1rem">A</span></button>
      <button @click=${() => this.fontSize = 200 }><span style="font-size: 1.3rem">A</span></button>
      <button @click=${() => this.fontSize = 300 }><span style="font-size: 1.7rem">A</span></button>
    </div>`;
  }
}

@customElement('app-version-picker')
export class VersionPicker extends LitElement {
  createRenderRoot = () => this

  @property({ type: Array }) versions: string[] = []

  render() {
    const available = [...versionMap.keys()].filter(k => !this.versions.includes(k))
    return html`
      <div class="version-picker">
        ${this.versions.map(v => html`
          <span class="version-tag">
            ${v}
            ${this.versions.length > 1 ? html`<button @click=${() => this._remove(v)}>&times;</button>` : ''}
          </span>
        `)}
        <select class="version-add-select" @change=${(e: Event) => this._add(e)}>
          <option value="">+ Add</option>
          ${available.map(v => html`<option value=${v}>${v}</option>`)}
        </select>
      </div>
    `
  }

  private _remove(version: string) {
    this.dispatchEvent(new CustomEvent('versions-changed', {
      detail: { versions: this.versions.filter(v => v !== version) }
    }))
  }

  private _add(e: Event) {
    const select = e.target as HTMLSelectElement
    if (!select.value) return
    this.dispatchEvent(new CustomEvent('versions-changed', {
      detail: { versions: [...this.versions, select.value] }
    }))
    select.value = ''
  }
}

const NT_BOOKS = new Set(['Matt','Mark','Luke','John','Acts','Rom','1Cor','2Cor','Gal','Eph','Phil','Col','1Thess','2Thess','1Tim','2Tim','Titus','Phlm','Heb','Jas','1Pet','2Pet','1John','2John','3John','Jude','Rev']);

@customElement('app-bible-nav')
export class BibleNav extends LitElement {
  createRenderRoot = () => this

  @property()
  versions: string[] = ['acv']

  @property()
  book: string = 'Gen'
  render() {
    const books = [...repositoryService.getUnifiedBooksList(this.versions)]
    const otBooks = books.filter(b => !NT_BOOKS.has(b))
    const ntBooks = books.filter(b => NT_BOOKS.has(b))

    const renderBook = (i: string) => html`<button
      @click=${() => this.selectBook(i)}
      class=${classMap({active: this.book === i})}>${i}</button>`

    return html`
      <div class="row">
        <div class="col">
          ${otBooks.length ? html`<span class="nav-section-label">OT</span>` : ''}
          ${otBooks.map(renderBook)}
          ${ntBooks.length ? html`<span class="nav-section-label">NT</span>` : ''}
          ${ntBooks.map(renderBook)}
        </div>
      </div>`;
  }

  selectBook(b: string){
    this.dispatchEvent(new CustomEvent('book-selected', {detail: {book: b}}))
  }
}

@customElement('app-theme-toggle')
export class ThemeToggle extends LitElement {
  createRenderRoot = () => this

  private _theme = new Setting<string>('theme', 'system')

  @property()
  get theme() { return this._theme.get() }
  set theme(v: string) {
    this._theme.set(v)
    this._applyTheme()
    this.requestUpdate()
  }

  connectedCallback() {
    super.connectedCallback()
    this._applyTheme()
  }

  private _applyTheme() {
    const t = this.theme
    if (t === 'system') {
      document.documentElement.removeAttribute('data-theme')
    } else {
      document.documentElement.setAttribute('data-theme', t)
    }
  }

  private _cycle() {
    const order = ['system', 'light', 'dark']
    const idx = order.indexOf(this.theme)
    this.theme = order[(idx + 1) % order.length]
  }

  private _icon() {
    switch (this.theme) {
      case 'light': return '\u2600\uFE0F' // sun
      case 'dark': return '\uD83C\uDF19' // moon
      default: return '\uD83D\uDCBB' // computer/system
    }
  }

  render() {
    return html`<button class="theme-toggle" @click=${() => this._cycle()} title="Theme: ${this.theme}">
      ${this._icon()}
    </button>`
  }
}
